import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  CheckCircle2,
  ArrowRight,
  Phone,
  Search,
  Users,
  FileSearch,
  Grid3x3,
  FlaskConical,
  ImageIcon,
  Building2,
  Globe2,
  Award,
  Upload
} from 'lucide-react';

export const GapAnalysisPackPage = () => {
  const problems = [
    'Missing Module 1 or Module 3 documents',
    'Outdated stability data',
    'Unacceptable labels or claims',
    'Incomplete CPP or GMP proof',
    'Wrong formulation declarations',
    'Incompatible pack inserts'
  ];

  const deliverables = [
    {
      icon: FileSearch,
      title: 'CTD/ACTD Module-by-Module Gap Report',
      lede: "Every module reviewed against your target country's checklist.",
      bullets: [
        'Module 1 (Administrative)',
        'Module 2 (Summaries)',
        'Module 3 (Quality)',
        'Module 4/5 (where applicable)'
      ]
    },
    {
      icon: Grid3x3,
      title: 'Document Availability Matrix',
      lede: 'Total clarity on what you have and what you are missing. Every document mapped as Available, Needs Update, or Missing.',
      bullets: []
    },
    {
      icon: FlaskConical,
      title: 'Stability & Quality Review',
      lede: 'Regulator-aligned review of stability commitments, storage claims, and shelf-life support across regions.',
      bullets: [
        'Stability expectations for each region',
        'Storage and labelling compliance',
        'Shelf-life support analysis'
      ]
    },
    {
      icon: ImageIcon,
      title: 'Artwork & Label Compliance',
      lede: 'Country-specific claim, language and declaration review for both pharma and nutra.',
      bullets: [
        'Claim review for both nutra and pharma',
        'Local language requirements',
        'Ingredient declaration norms'
      ]
    },
    {
      icon: Building2,
      title: 'Importer / Distributor Requirements',
      lede: 'A checklist of exactly what your local partner must provide.',
      bullets: []
    },
    {
      icon: Globe2,
      title: 'Country-by-Country Gap Summary',
      lede: 'Covering African, GCC, Latin American, ASEAN, and Indian Ocean markets, so multi-country submissions are aligned from the start.',
      bullets: []
    }
  ];

  const processSteps = [
    'Upload your current documents',
    "We review every module against each country's requirements",
    'You receive a gap report with ready-to-act fixes',
    'Final review call with a regulatory expert'
  ];

  const idealFor = [
    'Manufacturers with existing dossiers',
    'Brands wanting to revive old documentation',
    'Exporters preparing multi-country submissions',
    'Companies who have faced regulator deficiencies before'
  ];

  const faqs = [
    {
      q: 'What is a dossier gap analysis?',
      a: "A dossier gap analysis is a module-by-module review of your CTD or ACTD dossier against a target country's regulatory checklist, to identify every missing document, outdated study, or non-compliant element before you submit. It catches the issues that would otherwise return as a deficiency letter, so your submission clears faster."
    },
    {
      q: 'How does the Gap Analysis Pack prevent dossier rejection?',
      a: "It checks every CTD/ACTD module, maps each document as Available, Needs Update, or Missing, and reviews stability, artwork, labelling, and GMP/CPP evidence against each country's requirements. You fix the gaps before filing rather than after a rejection, which removes the most common causes of delay."
    },
    {
      q: 'What does the gap analysis cover?',
      a: 'It covers all CTD/ACTD modules, a full document availability matrix, stability and quality review, artwork and label compliance for both pharma and nutra, importer requirements, and a country-by-country gap summary across African, GCC, Latin American, ASEAN, and Indian Ocean markets.'
    },
    {
      q: 'I already have a dossier. Can you check it?',
      a: "Yes. The Gap Analysis Pack is built for manufacturers with existing dossiers, brands reviving old documentation, and companies that have faced regulator deficiencies before. Upload your current documents and we review them against each target country's requirements."
    },
    {
      q: 'What do I receive at the end?',
      a: 'You receive a gap report with ready-to-act fixes for every issue found, a document availability matrix, and a final review call with a regulatory expert to walk through the findings and next steps.'
    },
    {
      q: 'Do you review artwork and labels as well as the dossier?',
      a: 'Yes. The pack includes artwork and label compliance review covering claim acceptability for both pharma and nutra, local language requirements, and ingredient declaration norms, since non-compliant artwork is a frequent and avoidable cause of rejection.'
    }
  ];

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    'name': 'Gap Analysis Pack',
    'alternateName': 'Dossier Gap Analysis for CTD ACTD Regulatory Submissions',
    'serviceType': 'Pharmaceutical Dossier Gap Analysis (CTD / ACTD)',
    'description':
      "A module-by-module review of your CTD or ACTD dossier, artwork, stability, and GMP/CPP evidence against each target country's regulatory checklist, delivering a gap report with ready-to-act fixes before submission.",
    'provider': { '@type': 'Organization', 'name': 'TheApostrophe', 'url': 'https://www.theapostrophe.com' },
    'areaServed': ['Africa', 'GCC', 'Asia', 'Latin America', 'ASEAN', 'Indian Ocean'],
    'url': 'https://www.theapostrophe.com/success-packs/gap-analysis-pack',
    'offers': {
      '@type': 'Offer',
      'availability': 'https://schema.org/InStock',
      'url': 'https://www.theapostrophe.com/success-packs/gap-analysis-pack'
    }
  };

  const howToSchema = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    'name': 'How the Gap Analysis Pack works',
    'description': 'A four-step process to identify every missing document and compliance gap in your dossier before you file.',
    'step': processSteps.map((s, i) => ({
      '@type': 'HowToStep',
      'position': i + 1,
      'name': `Step ${i + 1}`,
      'text': s
    }))
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': faqs.map((f) => ({
      '@type': 'Question',
      'name': f.q,
      'acceptedAnswer': { '@type': 'Answer', 'text': f.a }
    }))
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': 'https://www.theapostrophe.com/' },
      { '@type': 'ListItem', 'position': 2, 'name': 'Success Packs', 'item': 'https://www.theapostrophe.com/success-packs' },
      { '@type': 'ListItem', 'position': 3, 'name': 'Gap Analysis Pack', 'item': 'https://www.theapostrophe.com/success-packs/gap-analysis-pack' }
    ]
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F0E0D0' }}>
      <Helmet>
        <title>Dossier Gap Analysis for CTD ACTD Regulatory Submissions</title>
        <meta
          name="description"
          content="Module-by-module dossier gap analysis for CTD and ACTD submissions. Find every missing document and compliance gap before you file and avoid regulator rejection."
        />
        <meta
          name="keywords"
          content="dossier gap analysis, regulatory dossier gap analysis, CTD ACTD gap analysis, dossier gap check, module-wise gap review, dossier development and gap analysis, avoid regulator rejection, document availability matrix, pharma dossier review, deficiency letter prevention"
        />
        <link rel="canonical" href="https://www.theapostrophe.com/success-packs/gap-analysis-pack" />

        <meta property="og:title" content="Dossier Gap Analysis for CTD ACTD Regulatory Submissions" />
        <meta property="og:description" content="Module-by-module dossier gap analysis for CTD and ACTD submissions. Find every missing document and compliance gap before you file." />
        <meta property="og:url" content="https://www.theapostrophe.com/success-packs/gap-analysis-pack" />
        <meta property="og:type" content="website" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Dossier Gap Analysis for CTD ACTD Regulatory Submissions" />
        <meta name="twitter:description" content="Find every missing document and compliance gap before regulators do. CTD/ACTD gap analysis for pharma & nutra exports." />

        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(howToSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      {/* Hero */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <Badge className="mb-6 px-6 py-2" style={{ backgroundColor: '#7090A0', color: 'white' }} data-testid="gap-pack-badge">
            Success Pack 3
          </Badge>
          <div className="w-24 h-24 rounded-2xl bg-white/60 flex items-center justify-center mb-6 mx-auto shadow-md">
            <Search className="h-12 w-12" style={{ color: '#7090A0' }} aria-hidden="true" />
          </div>
          <h1
            className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight"
            style={{ color: '#303040' }}
            data-testid="gap-pack-h1"
          >
            Dossier Gap Analysis: Find Every Gap Before Regulators Do
          </h1>
          <p className="text-xl md:text-2xl font-bold mb-8" style={{ color: '#7090A0' }}>
            Identify every missing document, module, and compliance gap before you start the registration process.
          </p>
          <p className="text-base md:text-lg mb-10 max-w-3xl mx-auto leading-relaxed" style={{ color: '#606070' }}>
            Most dossier submissions fail or get delayed for reasons that were visible before filing: a missing Module 3 study, outdated stability data, an unacceptable label claim, incomplete GMP proof. A dossier gap analysis catches all of it first. We run a full module-by-module check of your CTD/ACTD dossier, artwork, stability, and GMP/CPP evidence against each target country&apos;s requirements, so you enter registration fully ready and avoid the deficiency letters that cost months.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact#send-us-your-details">
              <Button
                size="lg"
                className="text-lg px-8 py-6 rounded-full transition-all duration-300 hover:scale-105"
                style={{ backgroundColor: '#7090A0', color: 'white' }}
                data-testid="gap-get-btn"
              >
                Get My Gap Analysis
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <a
              href="https://outlook.office.com/book/ScheduleaRegulatoryCall1@theapostrophe.in/?ismsaljsauthenabled=true"
              target="_blank"
              rel="noopener noreferrer external"
              onClick={(e) => {
                e.preventDefault();
                window.open(
                  'https://outlook.office.com/book/ScheduleaRegulatoryCall1@theapostrophe.in/?ismsaljsauthenabled=true',
                  '_blank',
                  'noopener,noreferrer'
                );
              }}
            >
              <Button
                size="lg"
                variant="outline"
                className="text-lg px-8 py-6 rounded-full transition-all duration-300 hover:scale-105"
                style={{ borderColor: '#7090A0', color: '#7090A0' }}
                data-testid="gap-consult-btn"
              >
                Schedule a Consultation
                <Phone className="ml-2 h-5 w-5" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* What It Solves */}
      <section className="py-20 px-6" style={{ backgroundColor: 'white' }}>
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center" style={{ color: '#303040' }}>
            What the Gap Analysis Pack Solves
          </h2>
          <p className="text-base md:text-lg text-center mb-12 max-w-3xl mx-auto" style={{ color: '#606070' }}>
            Most dossier submissions fail or get delayed because of:
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            {problems.map((p, i) => (
              <div
                key={i}
                className="flex items-start gap-3 p-6 rounded-xl transition-shadow hover:shadow-md"
                style={{ backgroundColor: '#F8F4EE' }}
              >
                <div
                  className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center font-bold text-white text-sm"
                  style={{ backgroundColor: '#7090A0' }}
                  aria-hidden="true"
                >
                  {i + 1}
                </div>
                <span className="text-base md:text-lg" style={{ color: '#505060' }}>
                  {p}
                </span>
              </div>
            ))}
          </div>
          <p className="text-base md:text-lg text-center mt-12 max-w-3xl mx-auto italic" style={{ color: '#606070' }}>
            This pack ensures you enter the registration phase fully ready, not hoping.
          </p>
        </div>
      </section>

      {/* What You Get - Deliverables */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center" style={{ color: '#303040' }}>
            What You Get
          </h2>
          <p className="text-base md:text-lg text-center mb-16 max-w-3xl mx-auto" style={{ color: '#606070' }}>
            Six deliverables that leave no module, no document, and no market untouched.
          </p>
          <div className="space-y-10">
            {deliverables.map((d, i) => (
              <article
                key={i}
                className="rounded-3xl p-8 md:p-12 shadow-md hover:shadow-xl transition-shadow"
                style={{ backgroundColor: 'white' }}
              >
                <div className="grid md:grid-cols-[auto_1fr] gap-8 items-start">
                  <div className="flex md:flex-col items-start gap-4 md:gap-3">
                    <div
                      className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: '#7090A0' }}
                    >
                      <d.icon className="h-8 w-8 text-white" aria-hidden="true" />
                    </div>
                    <div className="text-4xl font-bold" style={{ color: '#7090A0', opacity: 0.5 }}>
                      0{i + 1}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl md:text-2xl font-bold mb-4" style={{ color: '#303040' }}>
                      {d.title}
                    </h3>
                    <p className="text-base md:text-lg leading-relaxed mb-6" style={{ color: '#606070' }}>
                      {d.lede}
                    </p>
                    {d.bullets.length > 0 && (
                      <div className="space-y-2">
                        {d.bullets.map((b, bi) => (
                          <div key={bi} className="flex items-start gap-3">
                            <CheckCircle2 className="h-5 w-5 mt-1 flex-shrink-0" style={{ color: '#7090A0' }} aria-hidden="true" />
                            <span className="text-base" style={{ color: '#505060' }}>
                              {b}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>
          <p className="text-center text-base md:text-lg mt-12 max-w-3xl mx-auto italic font-medium" style={{ color: '#7090A0' }}>
            Every gap comes with a ready-to-act fix, not just a flag.
          </p>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 px-6" style={{ backgroundColor: 'rgba(112, 144, 160, 0.08)' }}>
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-16 text-center" style={{ color: '#303040' }}>
            Process
          </h2>
          <ol className="space-y-6">
            {processSteps.map((step, i) => (
              <li key={i} className="flex items-start gap-6 p-6 rounded-2xl" style={{ backgroundColor: 'white' }}>
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-white text-lg"
                  style={{ backgroundColor: '#7090A0' }}
                  aria-hidden="true"
                >
                  {i + 1}
                </div>
                <p className="text-base md:text-lg leading-relaxed pt-2" style={{ color: '#505060' }}>
                  {step}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Ideal For */}
      <section className="py-24 px-6" style={{ backgroundColor: 'white' }}>
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-center gap-3 mb-8">
            <Users className="h-8 w-8" style={{ color: '#7090A0' }} aria-hidden="true" />
            <h2 className="text-3xl md:text-4xl font-bold" style={{ color: '#303040' }}>
              Ideal For
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-4 max-w-4xl mx-auto">
            {idealFor.map((item, i) => (
              <div key={i} className="flex items-start gap-3 p-6 rounded-xl" style={{ backgroundColor: '#F8F4EE' }}>
                <CheckCircle2 className="h-6 w-6 mt-0.5 flex-shrink-0" style={{ color: '#7090A0' }} aria-hidden="true" />
                <span className="text-base md:text-lg" style={{ color: '#505060' }}>
                  {item}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why This Matters */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="p-8 md:p-12 rounded-3xl" style={{ backgroundColor: 'rgba(112, 144, 160, 0.12)' }}>
            <div className="flex items-center gap-3 mb-6">
              <Award className="h-7 w-7" style={{ color: '#7090A0' }} aria-hidden="true" />
              <h2 className="text-2xl md:text-3xl font-bold" style={{ color: '#7090A0' }}>
                Why This Matters
              </h2>
            </div>
            <p className="text-lg md:text-xl leading-relaxed" style={{ color: '#505060' }}>
              A perfect dossier starts with a perfect gap plan. This pack prevents deficiency letters, unexpected delays, and repeated rejection cycles. Every gap you close now is a month you do not lose later, and a submission that clears the first time.
            </p>
          </div>
        </div>
      </section>

      {/* Retainer CTA Block */}
      <section className="py-24 px-6" style={{ backgroundColor: 'rgba(160, 96, 176, 0.08)' }}>
        <div className="max-w-5xl mx-auto">
          <div className="rounded-3xl p-8 md:p-12 shadow-xl" style={{ backgroundColor: 'white' }}>
            <div className="flex flex-col md:flex-row items-start md:items-center gap-8">
              <div
                className="w-20 h-20 rounded-2xl flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: '#A060B0' }}
              >
                <Users className="h-10 w-10 text-white" aria-hidden="true" />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl md:text-3xl font-bold mb-4" style={{ color: '#303040' }}>
                  Want your entire regulatory workflow handled continuously?
                </h2>
                <p className="text-base md:text-lg leading-relaxed mb-6" style={{ color: '#708080' }}>
                  Closing gaps once is good. Keeping every dossier gap-free across markets is what the <strong>Apostrophe Retainer</strong> delivers, with one dedicated regulatory affairs expert managing every stage on an ongoing basis, so you never have to hire, train, or run an in-house regulatory team.
                </p>
                <Link to="/retainer">
                  <Button
                    size="lg"
                    className="px-6 py-5 rounded-full transition-all duration-300 hover:scale-105"
                    style={{ backgroundColor: '#A060B0', color: 'white' }}
                    data-testid="gap-retainer-btn"
                  >
                    Explore the Retainer Plan
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-6" style={{ backgroundColor: 'rgba(112, 144, 160, 0.08)' }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: '#303040' }}>
            Ready to Close Your Gaps?
          </h2>
          <p className="text-lg mb-10" style={{ color: '#606070' }}>
            Upload your current dossier and we will show you exactly what stands between you and a clean submission.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact#send-us-your-details">
              <Button
                size="lg"
                className="text-lg px-8 py-6 rounded-full transition-all duration-300 hover:scale-105"
                style={{ backgroundColor: '#7090A0', color: 'white' }}
                data-testid="gap-cta-report-btn"
              >
                Get My Gap Report
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/contact#send-us-your-details">
              <Button
                size="lg"
                variant="outline"
                className="text-lg px-8 py-6 rounded-full transition-all duration-300 hover:scale-105"
                style={{ borderColor: '#7090A0', color: '#7090A0' }}
                data-testid="gap-cta-upload-btn"
              >
                Upload My Current Dossier
                <Upload className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ - AEO */}
      <section className="py-24 px-6" style={{ backgroundColor: 'white' }}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center" style={{ color: '#303040' }}>
            Frequently Asked Questions
          </h2>
          <p className="text-center text-base md:text-lg mb-16" style={{ color: '#708080' }}>
            Straight answers about CTD / ACTD gap analysis, deficiency prevention, and dossier readiness.
          </p>
          <div className="space-y-6">
            {faqs.map((f, i) => (
              <details
                key={i}
                className="group rounded-2xl p-6 md:p-8 transition-shadow hover:shadow-md"
                style={{ backgroundColor: '#F8F4EE' }}
                open={i === 0}
              >
                <summary
                  className="cursor-pointer list-none flex items-start justify-between gap-4"
                  data-testid={`gap-faq-${i}`}
                >
                  <h3 className="text-lg md:text-xl font-bold" style={{ color: '#303040' }}>
                    {f.q}
                  </h3>
                  <span
                    className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center transition-transform group-open:rotate-45"
                    style={{ backgroundColor: '#7090A0', color: 'white' }}
                    aria-hidden="true"
                  >
                    +
                  </span>
                </summary>
                <p className="text-base md:text-lg leading-relaxed mt-4" style={{ color: '#606070' }}>
                  {f.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};
